import { ICON_MAPPING, getIconComponent } from "@storytell/bsky-icons";
import { Icons } from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { LabelId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import type { IconTypes } from "solid-icons";
import { TbX } from "solid-icons/tb";
import { onCleanup, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LabelTooltip } from "~/components/labels/LabelTooltip";
import { isLightColor } from "~/components/layouts/minimal/isLightColor";
import { useWire } from "~/wire";

export const VariableV1Label = (props: {
  notInPromptBar?: boolean;
  id: string;
  selected?: boolean;
  onDelete: () => void;
  isEditable?: boolean;
  text?: string;
}) => {
  const wire = useWire();
  const label = () => wire.labels.get(props.id as LabelId);

  // TODO: @andi, Better way to communicate between prompt bar and app state?
  onMount(() => {
    if (props.notInPromptBar) return;
    const unsubscribe = subscribeToGlobalEvent("removed-item-from-prompt-scope", (event) => {
      if (event.detail.id === props.id) {
        props.onDelete();
      }
    });
    dispatchGlobalEvent("added-mention-to-prompt-bar", {
      id: props.id,
    });
    onCleanup(() => {
      unsubscribe();
      dispatchGlobalEvent("removed-mention-from-prompt-bar", {
        id: props.id,
      });
    });
  });

  const truncatedName = () => {
    const maxLength = 40;
    const name = label()?.name || props.text || "Unknown";
    if (name.length <= maxLength) return name;

    const extIndex = name.lastIndexOf(".");
    const extension = extIndex !== -1 ? name.slice(extIndex) : "";
    const nameWithoutExt = extIndex !== -1 ? name.slice(0, extIndex) : name;

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    if (charsToShow <= 0) return name;

    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    return `${nameWithoutExt.slice(0, frontChars)}...${nameWithoutExt.slice(nameWithoutExt.length - backChars)}${extension}`;
  };

  const labelName = label()?.name || props.text || "Unknown";

  return (
    <span
      onClick={(e) => {
        // Don't navigate if clicking the delete button
        if ((e.target as HTMLElement).closest("button")) return;
        e.preventDefault();
        // Clear any selected concept first to ensure we show the assets table
        dispatchGlobalEvent("clear-selected-concept", undefined);
        // Dispatch event - the handler in KnowledgePane will open the pane and apply the filter
        dispatchGlobalEvent("open-label-in-assets-pane", { labelId: props.id });
      }}
      class={twMerge(
        "group/mention inline-flex items-center gap-1 rounded-lg bg-tile hover:bg-tile-hover text-sm text-primary-text border border-primary-border px-1 cursor-pointer",
        props.selected && "ring-2 ring-brand",
      )}
    >
      <span
        class="rounded-full size-4 grid place-content-center bg-[var(--label-color)] group-hover/mention:hidden"
        style={{
          "--label-color": label()?.color,
          "--label-icon": isLightColor(label()?.color || "") ? "black" : "white",
        }}
      >
        <Dynamic
          component={getIconComponent(label()?.icon || "QUESTION_MARK") as IconTypes}
          class="text-[var(--label-icon)] transition-transform"
        />
      </span>
      <Show when={props.isEditable}>
        <StTooltip placement="top" contentProps={{ class: "z-20" }} content={<p>Remove</p>}>
          <StTooltipTrigger
            as="button"
            onClick={() => {
              props.onDelete();
            }}
            type="button"
            class="hidden group-hover/mention:flex items-center gap-1 transition-transform cursor-pointer"
          >
            <TbX class="size-3 transition-transform text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
            <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
          </StTooltipTrigger>
        </StTooltip>
      </Show>
      <span class="hover:font-bold">{truncatedName()}</span>
    </span>
  );
};
